import { Core } from "@faire/design-tokens";
import {
  NAVIGATION_FROM,
  SEARCH_SECTION_FROM,
} from "@faire/help-center-utils/consts/eventEnum";
import { CustomerTypes } from "@faire/help-center-utils/enums/CustomerType";
import { useCustomerType } from "@faire/help-center-utils/hooks/useCustomerType";
import { localeString } from "@faire/help-center-utils/lib/localeString";
import { getBrandToken } from "@faire/help-center-utils/serialized-data/getBrand";
import { getRetailerToken } from "@faire/help-center-utils/serialized-data/getRetailer";
import { isHiddenSection } from "@faire/help-center/consts";
import { useZDCategoryId } from "@faire/help-center/hooks/useZDCategoryId";
import { Search } from "@faire/web--source/slate/icons/foundation/Search";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { TypographyStyles } from "@faire/web--source/slate/Typography";
import { useState } from "@faire/web--source/ui/hooks/useState";
import searchArticles, {
  QueryParameters as ISearchArticlesRequest,
} from "@faire/web-api--source/endpoints/www/api/zendesk/articles/search/get";
import { ISearchArticlesResponse } from "@faire/web-api--source/com/faire/vendor/zendesk/ISearchArticlesResponse";
import { IZendeskArticle } from "@faire/web-api--source/com/faire/vendor/zendesk/IZendeskArticle";
import {
  trackHelpCenterSearchArticleKeyboardInput,
  trackHelpCenterSearchArticleKeyboardInputTraceApiRequestEnd,
  trackHelpCenterSearchArticleKeyboardInputTraceApiRequestStart,
} from "@faire/web-api--source/events/helpCenter/keyboardInput/searchArticle";
import { trackHelpCenterSearchEnterKeyboardInput } from "@faire/web-api--source/events/helpCenter/keyboardInput/searchEnter";
import articlePage from "@faire/web-api--source/routes/www/support/articles/articleId";
import searchPage from "@faire/web-api--source/routes/www/support/search";
import get from "lodash/get";
import qs from "query-string";
import React from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { useInitialSuggestedSearchResult } from "./hooks/useInitialSuggestedSearchResult";
import { useIsSearchInputAtHeader } from "./hooks/useIsSearchInputAtHeader";
import { useSearchInputPlaceholder } from "./hooks/useSearchInputPlaceholder";
import { SearchResult } from "./SearchResult";

export interface IRoundSearchInputProps {
  width?: string;
  value?: string;
  navigatedFrom?: NAVIGATION_FROM;
  sectionFrom?: SEARCH_SECTION_FROM;
  id?: string;
}

export interface ISearchResult {
  id: string;
  title: string;
}

export const RoundSearchInput: React.FC<IRoundSearchInputProps> = ({
  width,
  value,
  navigatedFrom,
  sectionFrom,
  id,
}) => {
  const categoryId = useZDCategoryId();
  const [searchQuery, setSearchQuery] = useState<string>(value ?? "");
  const [showResult, setShowResult] = useState<boolean>(false);
  const [selected, setSelected] = useState<number>(-1);
  const [searchResult, setSearchResult] = useState<ISearchResult[]>([]);
  const history = useHistory();
  const placeholder = useSearchInputPlaceholder(sectionFrom);
  const { customerType } = useCustomerType();
  const initSearchResult = useInitialSuggestedSearchResult();
  const abortControllerRef = React.useRef<AbortController>(
    new AbortController()
  );
  const hasSearchResult = !!searchResult.length;
  const isSearchInputAtHeader = useIsSearchInputAtHeader(sectionFrom);

  const goToSearchPage = () => {
    history.push(
      `${searchPage()}?${qs.stringify({
        categoryId: categoryId,
        searchTerm: searchQuery,
      })}`
    );
  };

  const handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    const keyCode = event.key;
    if (keyCode === "ArrowDown" && selected < searchResult.length - 1) {
      setSelected(selected + 1);
    } else if (keyCode === "ArrowUp" && selected > 0) {
      setSelected(selected - 1);
    } else if (keyCode === "Enter" && searchQuery) {
      if (selected !== -1) {
        search();
      } else {
        trackHelpCenterSearchEnterKeyboardInput(
          history.location?.pathname ?? "",
          sectionFrom
        );
        history.push(
          `${searchPage()}?${qs.stringify({
            categoryId: categoryId,
            searchTerm: searchQuery,
          })}`
        );
      }
    }
  };

  const search = () => {
    const selectedArticle = searchResult[selected];
    if (selectedArticle) {
      history.push(articlePage(selectedArticle.id));
      resetSearch();
    }
  };

  const resetSearch = () => {
    toggleResult(true);
    setSearchQuery("");
    setSelected(-1);
  };

  const initSearchState = (value: string) => {
    setSearchQuery(value);
    setSelected(-1);
  };

  const handleSearch = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const value = event.currentTarget.value;
      initSearchState(value);
      if (!value || value.length < 3) {
        setSearchResult([]);
        return;
      }

      trackHelpCenterSearchArticleKeyboardInputTraceApiRequestStart();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      const response: ISearchArticlesResponse = await searchArticles(
        ISearchArticlesRequest.build({
          query: value,
          category: categoryId,
          locale: localeString,
        }),
        {
          signal: abortController.signal,
        }
      );

      trackHelpCenterSearchArticleKeyboardInputTraceApiRequestEnd();
      const result = get(response, "results", []);
      setSearchResult(
        result
          .filter(
            (article: IZendeskArticle) =>
              !isHiddenSection(article.section_id) && !article.draft
          )
          .map((article: IZendeskArticle) => ({
            id: article.id?.toString() ?? "",
            title: article.title ?? "",
            snippet: article.snippet ?? "",
          }))
      );
      if (result.length) {
        toggleResult();
        // when we have search result, we track the search input
        trackHelpCenterSearchArticleKeyboardInput(
          customerType === CustomerTypes.BRAND ? "brand" : "retailer",
          getRetailerToken() ?? "",
          getBrandToken() ?? "",
          searchQuery,
          sectionFrom
        );
      }
    } catch (err) {
      setSearchResult([]);
      toggleResult(true);
    }
  };

  const onBlur = (event: React.FormEvent<HTMLElement>) => {
    const currentTarget = event.currentTarget;
    setTimeout(() => {
      if (!currentTarget.contains(document.activeElement)) {
        toggleResult(true);
      }
    }, 0);
  };

  const toggleResult = (hide?: boolean) => {
    setShowResult(!hide);
  };

  return (
    <Wrapper $width={width} onBlur={(event) => onBlur(event)}>
      <RoundSearchWrapper
        $isSearchInputAtHeader={isSearchInputAtHeader}
        id={id}
      >
        {isSearchInputAtHeader ? <Spacer width="1x" /> : null}
        <Spacer width="1x" />
        <Search color={Core.icon.default} />
        <Spacer width="1x" />
        <SearchInput
          data-test-id={id}
          type="text"
          placeholder={placeholder}
          onChange={handleSearch}
          onKeyDown={handleSearchKeyDown}
          value={searchQuery}
          onFocus={() => toggleResult(false)}
        />
      </RoundSearchWrapper>
      {showResult ? (
        <SearchResult
          isSearchInputAtHeader={isSearchInputAtHeader}
          searchQuery={searchQuery}
          hasSearchResult={hasSearchResult}
          searchResult={searchResult.length ? searchResult : initSearchResult}
          navigatedFrom={navigatedFrom}
          sectionFrom={sectionFrom}
          resetSearch={resetSearch}
          selected={selected}
          setSelected={setSelected}
          goToSearchPage={goToSearchPage}
        />
      ) : null}
    </Wrapper>
  );
};

const Wrapper = styled(Column)<{ $width?: string }>`
  position: relative;
  width: 100%;

  ${responsiveMediaQueries.tabletAndAbove} {
    width: ${(props) => props.$width ?? "608px"};
  }
`;

const RoundSearchWrapper = styled.div<{ $isSearchInputAtHeader: boolean }>`
  display: flex;
  align-items: center;
  border-radius: 25px;
  ${({ $isSearchInputAtHeader }) =>
    $isSearchInputAtHeader ? "padding: 0px;" : "padding: 5px;"}
  border: 1px solid ${Core.border.muted};
  background-color: ${Core.surface.inverse};
  transition: all 0.3s ease;
`;

const SearchInput = styled.input`
  ${TypographyStyles({ variant: "paragraphSansRegular" })}
  border: none;
  outline: none;
  background-color: transparent;
  flex: 1;
  height: 40px;
`;
